import './App.less';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { PROJECT_CATEGORY } from './declare/enums/project-category';
import UploadPanel, { UploadFileState, useUploadService } from './panel/upload-panel';
import DownloadPanel, { DownloadState, useDownloadService } from './panel/download-panel';
import { createContext, useRef, useState } from 'react';
import { useEffect } from 'react';
import localStore, { USER_ID, USER_NAME, USER_UUID } from './store/local-store';
import { userCheckLogin } from './declare/api/user';
import { User } from './declare/interface/user';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { SystemStatus, useSystemService } from './store/system-store';
import UserFeedBackPanel from './components/user-feedback';
import { useFeedbackUploadService, UserFeedbackUploadFileState } from './components/user-feedback/upload-panel';
import { getRsUserAuths, getRsUserFileAuths } from './declare/api/permission';
import { UserAuthStatus, useUserAuthService } from './store/auth-store';
import { ConfigProvider, message } from 'antd';
import { isRecruitStatus } from './declare/enums/user-status';
import zhCN from 'antd/es/locale/zh_CN';
import { emitter, EMITTER_TYPE } from './store/mitt';
import UserUpdateLogPanel from './components/user-updateLog';
import RequireAuth from './components/route-auth';
import { Suspense, lazy } from 'react';

const ProjectLayout = lazy(() => import('./layout/project'));
const LoginLayout = lazy(() => import('./layout/login'));
const FileSharePanel = lazy(() => import('./panel/file-panel/file-share'));
const FilePanel = lazy(() => import('./panel/file-panel'));
const QueryPage = lazy(() => import('./panel/query-page'));
const RecruitLayout = lazy(() => import('./layout/recruit'));
const ForgetPwdPage = lazy(() => import('./layout/forget'));

export const WsConfig = {
    // TODO: build 部署时要修改ws地址端口
    address: process.env.NODE_ENV === 'development' ?
        `ws://localhost` :
        `wss://teamerpapp.tncet.com`,
    port: process.env.NODE_ENV === 'development' ? ':9557' : '',

}

export const SystemContext = createContext<SystemStatus>(null);
export const UploadFileContext = createContext<UploadFileState>(null);
export const DownloadContext = createContext<DownloadState>(null);
export const UserFeedbackUploadFileContext = createContext<UserFeedbackUploadFileState>(null);
export const UserAuthContext = createContext<UserAuthStatus>(null);

function App() {

    const navigate = useNavigate();
    const location = useLocation(); // 获取当前路由的路径
    const systemContext = useSystemService();
    const userAuthContext = useUserAuthService();
    const [visible, setVisible] = useState(false);  // 意见反馈弹窗
    const [updateLogVisible, setUpdateLogVisible] = useState(false); // 更新日志弹窗
    const pathnameRef = useRef<string>(null);

    useEffect(() => {
        pathnameRef.current = location.pathname;
    }, [location.pathname])

    // 增加监听无权限登录异常
    useEffect(() => {
        const onCheckLogin = () => {
            if (!location.pathname.includes('login')
                && !location.pathname.includes('recruit')
                && !location.pathname.includes('share')
                && !location.pathname.includes('forget')
                && !location.pathname.includes('public-folder')) {
                localStore.clear();
                navigate('/login')
            }
            // navigate('/login')
        };

        emitter.on(EMITTER_TYPE.UN_AUTHORIZED, onCheckLogin);

        return () => {
            emitter.off(EMITTER_TYPE.UN_AUTHORIZED, onCheckLogin);
        };
    }, []);

    let timer$;

    /**
     * 定期校验登录
     */
    useEffect(() => {
        checkLogin();
        timer$ = setInterval(() => {
            checkLogin();
        }, 90000)
        return (() => {
            clearInterval(timer$);
        })
    }, [systemContext.rws])

    /**
     * 校验登录
     * @returns 
     */
    const checkLogin = () => {
        let token = localStore.token || '';
        if (token.length <= 0) {
            if (!location.pathname.includes('login')
                && !location.pathname.includes('recruit')
                && !location.pathname.includes('share')
                && !location.pathname.includes('forget')
                && !location.pathname.includes('public-folder')) {
                localStore.clear();
                navigate('/login')
            }
            return;
        }
        userCheckLogin()
            .then(res => {
                let userDetail: User = res.data;
                localStore.set(USER_ID, userDetail.id);
                localStore.set(USER_UUID, userDetail.uuid);
                localStore.set(USER_NAME, userDetail.name);

                getRsUserAuths(localStore.userId).then(res => {
                    userAuthContext.onUserAuthsChange(res.data || []);
                })

                getRsUserFileAuths(localStore.userId).then(res => {
                    userAuthContext.onUserFileAuthsChange(res.data || []);
                })

                if (userDetail.userProject) {
                    localStore.userProjectId = userDetail.userProject.id;
                }
                if (systemContext.rws == null) {
                    initWs();
                }
                if (isRecruitStatus(userDetail.userStatus) && !location.pathname.includes('recruit')) {
                    navigate('/recruit');
                    return;
                }
                if (pathnameRef.current.includes('login')) {
                    // 如果当前在登录页，自动跳转到项目页
                    navigate('/projects');
                }

            }).catch(err => {
                if (!location.pathname.includes('recruit') && !location.pathname.includes('forget')) {
                    localStore.clear();
                    navigate('/login')
                }
            })
    }

    const wsMsg = (value) => {
        emitter.emit(EMITTER_TYPE.WS, value);
    }

    useEffect(() => {
        setTimeout(() => {
            if (systemContext.rws != null) {
                systemContext.rws.onmessage = wsMsg;
            }
        }, 0);
    }, [systemContext.rws, wsMsg])


    const initWs = () => {
        setTimeout(() => {
            const userUuid = localStore.userUuid;
            if (userUuid == null) return;
            let rws = new ReconnectingWebSocket(`${WsConfig.address}${WsConfig.port}/api-teamerp/ws?uuid=${userUuid}&device=WEB`);
            systemContext.resetRws(rws);
        }, 0)
    }



    return (
        <ConfigProvider locale={zhCN}>
            <SystemContext.Provider value={systemContext}>
                <DownloadContext.Provider value={useDownloadService()}>
                    <UploadFileContext.Provider value={useUploadService()}>
                        <UserAuthContext.Provider value={userAuthContext}>
                            <UserFeedbackUploadFileContext.Provider value={useFeedbackUploadService()}>
                                <div className="tn-base">
                                    <Suspense fallback={<div>Loading...</div>}><Routes>
                                        <Route path='/login/*' element={<LoginLayout />} />
                                        <Route path='/forget/*' element={<ForgetPwdPage />} />

                                        <Route element={<RequireAuth />}>
                                            <Route path='/own/*' element={<ProjectLayout type={PROJECT_CATEGORY.PERSONAL} feedbackVisible={visible} changeFeedbackVisible={(value) => setVisible(value)} updateLogVisible={updateLogVisible} changeUserUpdateLogVisible={(value) => setUpdateLogVisible(value)} />} />
                                            <Route path='/projects/:projectId/*' element={<ProjectLayout type={PROJECT_CATEGORY.PROJECT} feedbackVisible={visible} changeFeedbackVisible={(value) => setVisible(value)} updateLogVisible={updateLogVisible} changeUserUpdateLogVisible={(value) => setUpdateLogVisible(value)} />} />
                                            {/* <Route path='/functional-projects/:projectId/*' element={<ProjectLayout type={PROJECT_CATEGORY.FUNCTIONAL} feedbackVisible={visible} changeFeedbackVisible={(value) => setVisible(value)} />} /> */}
                                            <Route path='/institutions/:institutionId/*' element={<ProjectLayout type={PROJECT_CATEGORY.INSTITUTION} feedbackVisible={visible} changeFeedbackVisible={(value) => setVisible(value)} updateLogVisible={updateLogVisible} changeUserUpdateLogVisible={(value) => setUpdateLogVisible(value)} />} />
                                            <Route path='/library/*' element={<ProjectLayout type={PROJECT_CATEGORY.LIBRARY} feedbackVisible={visible} changeFeedbackVisible={(value) => setVisible(value)} updateLogVisible={updateLogVisible} changeUserUpdateLogVisible={(value) => setUpdateLogVisible(value)} />} />
                                            {/* <Route path='/test/*' element={<TestPanel />} ></Route> */}
                                            <Route path='/share/*' element={<FileSharePanel />} ></Route>
                                            <Route path='/share/:shareId' element={<FileSharePanel />} ></Route>
                                            {/* <Route path='/public-folder' element={<PublicFolder />} /> */}
                                            <Route path='files/:parentId' element={<FilePanel projectId={`492`} needAuth />} ></Route>
                                            <Route path='files' element={<FilePanel projectId={`492`} needAuth />} ></Route>
                                            <Route path='/standard-query/*' element={<QueryPage />} ></Route>
                                        </Route>
                                        <Route path='/recruit/*' element={<RecruitLayout />} />
                                        <Route path="*" element={<Navigate to={`/own`} />} />
                                    </Routes></Suspense>
                                </div>
                                <DownloadPanel />
                                <UploadPanel />
                                <UserUpdateLogPanel visible={updateLogVisible} changeVisible={(value) => setUpdateLogVisible(value)} />
                                <UserFeedBackPanel visible={visible} changeVisible={(value) => setVisible(value)} />
                            </UserFeedbackUploadFileContext.Provider >
                        </UserAuthContext.Provider >
                    </UploadFileContext.Provider>
                </DownloadContext.Provider>
            </SystemContext.Provider>
        </ConfigProvider>

    );
}

export default App;
